/**
 * The EditorForm component
 */
import React, { useEffect, useState } from "react";
import { FormProvider, useForm, Controller } from "react-hook-form";

import {
  Container,
  Row,
  Col,
  Button,
  Modal,
  ModalBody,
  ModalHeader,
  ModalFooter,
  ListGroupItem,
  ListGroup,
  Card,
  CardText,
  CardImg,
  CardTitle,
  CardBody,
  CardSubtitle,
  FormGroup,
  Label,
  Input,
  NavLink,
} from "reactstrap";

import AddToCartModal from "../CheckoutCart/utils/AddToCartModal";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import EditorFormSection from "../EditorFormSection";
import EditorDiscountTable from "../EditorDiscountTable";
import FileUploadPreview from "./utils/FileUploadPreview";
import LaminateField from "./utils/LaminateField";
import CuttingField from "./utils/CuttingField";
import WhiteprintModeField from "./utils/WhiteprintModeField";
import FileCheckField from "./utils/FileCheckField";
import AmountSelectField from "./utils/AmountSelectField";
import ColorSelectField from "../ColorSelectField";
import { isServer } from "../../redux/store";
import { REACT_APP_COP_SERVER } from "../../utils/env";

import {
  upload as uploadFile,
  uploadSuccess,
  reset as resetFileUpload,
  startCrop,
  endCrop,
  resetUploadFail,
} from "../../redux/cop/artworks";

import {
  addItemToCart,
  resetCartState as resetCart,
} from "../../redux/shop/cart";

import notificationsActions from "../../redux/notifications";
import { COLORTYPES, COLORS } from "../../utils/colors";
import { FILEEDITOR, FILEEDITORTYPES } from "./utils/fileEditor";
import fileguideIcon from "../../assets/images/icons/fileguide_colored.svg";
import icon_upload from "../../assets/images/icons/upload.svg";
import icon_vectorfile from "../../assets/images/icons/vector-file.svg";
// import yourText_negative_en from '../../assets/images/yourText-negative-en.svg';
import EditorFormHelpers from "./utils/FileUploadEditorFormHelpers";
import FormattedNumber from "../FormattedNumber";
import instructions from "../../assets/images/file_uploader_logo.png";

import {
  FaStar,
  FaStarHalf,
  FaTimes,
  FaChevronRight,
  FaExclamationTriangle,
} from "react-icons/fa";
import { push } from "connected-react-router";
import DataLayerHelper from "../../utils/dataLayer";
import { resetCookieError } from "../../redux/shop/errors";
import SizeSection from "./sections/SizeSection";
import SizeSelectSection from "./sections/SizeSelectSection";
import LaminateSection from "./sections/LaminateSection";
import SummarySection from "./sections/SummarySection";
import CuttingSection from "./sections/CuttingSection";
import AdditionalOptionsSection from "./sections/AdditionalOptionsSection";
import FileCheckSection from "./sections/FileCheckSection";
import ProductSelectionSection from "./sections/ProductSelectionSection";
import FileSizeModal from "./utils/FileSizeModal";
import { createProject, resetCreateFail } from "../../redux/cop/projects";
import calculateOptimizedSizeProposal from "./utils/calculateOptimizedSizeProposal";
import WhiteprintModeSection from "./sections/WhitePrintModeSection";
import VinylSection from "./sections/VinylSection";
import ColorSection from "./sections/ColorSection";

// Allowed formats for VectorizeCheck
const allowedFormats = ["AI", "SVG", "EPS", "PDF", "PSD", "PNG", "JPG", "GIF"];

/**
 * Form to handle update and create items
 */

const FileUploadEditorForm = (props, context) => {
  // Form Stuff
  const { initialValues, disablePreview, fileJson, change, pristine } = props;
  const methods = useForm({ defaultValues: initialValues, mode: "onChange" });
  const {
    control,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { isDirty },
  } = methods;
  // locale states
  const [open, setOpen] = useState("size");
  const [sizeChanged, setSizeChanged] = useState(
    initialValues?.isSizePresetted
  );
  const [fileSizeModal, setFileSizeModal] = useState(false);
  const [handledFileSize, setHandledFileSize] = useState(false);
  const [vectorizeFileModal, setVectorizeFileModal] = useState(false);
  const [handledVectorizeFile, setHandledVectorizeFile] = useState(false);
  const [skipFileUploadTouched, setSkipFileUploadTouched] = useState(false);
  const [addToCartErrorModal, setAddToCartErrorModal] = useState(false);
  const [addToCartWarningModal, setAddToCartWarningModal] = useState(false);
  const [sizeSelectModalIsOpen, setSizeSelectModalIsOpen] = useState(
    initialValues?.preopenSizeSelection
  );
  const [sizeChangeActive, setSizeChangeActive] = useState(false);

  // React Hook Form Values
  const width = watch("width");
  const height = watch("height");
  const cutting = watch("cutting");
  const editorType = watch("editorType");
  const artworkId = watch("artworkId");
  const keepRatio = watch("keepRatio");
  const fileRatio = watch("afileRatiortworkId");
  const amount = watch("amount");
  const materialSerie = watch("materialSerie");
  const color = watch("color");
  const skip_file_upload = watch("skip_file_upload");

  // Redux State Stuff
  const dispatch = useDispatch();
  const {
    language,
    addingToCart,
    addedToCart,
    addToShopCartSuccess,
    addToCartProcessing,
    addToCartError,
    cookieError,
    cart,
    cropping,
  } = useSelector((state) => ({
    size: state.editorSize,
    language: state.i18nState.lang,
    addingToCart: state.projects.creating, // new COP endpoint
    addedToCart: state.editorShop.loaded,
    addToShopCartSuccess: state.shopCart.addToShopCartSuccess,
    addToCartProcessing:
      state.projects.creating || state.shopCart.addToCartProcessing,
    addToCartError: state.shopCart.error,
    cookieError: state.shopErrors.cookieError,
    cart: state.shopCart.cart,
    cropping: state.artworks.cropping,
  }));

  const {
    artwork,
    currentArtworkId,
    uploadProgress,
    artworkUploadError,
    projectCreateError,
  } = useSelector((state) => ({
    artwork: state.artworks.byIdDictionary[artworkId],
    currentArtworkId: state.artworks.currentArtworkId,
    uploadProgress: state.artworks.uploadProgress,
    projectCreateError: state.projects.createError,
    artworkUploadError: state.artworks.uploadError,
  }));

  const { file, sku, vectorizeFile } = useSelector((state) => ({
    underglass: state.form?.fileEditorForm?.values?.underglass,
    border: state.form?.fileEditorForm?.values?.border,
    height: state.form?.fileEditorForm?.values?.height,
    file: state.editorFile,
    filecheck: state.form?.fileEditorForm?.values?.filecheck,
    keepRatio: state.form?.fileEditorForm?.values?.keepRatio,
    fileRatio: state.form?.fileEditorForm?.values?.fileRatio,
    sku: state.form?.fileEditorForm?.values?.sku,
    calculatedDimension:
      state.form?.fileEditorForm?.values?.calculatedDimension,
    amount: state.form?.fileEditorForm?.values?.amount,
    vectorizeFile: state.form?.fileEditorForm?.values?.vectorizeFile,
  }));

  // Translations Dummy Object
  const translations = {
    title: context.t("Text"),
    yes: context.t("Yes"),
    no: context.t("No"),
    width: context.t("Width"),
    height: context.t("height"),
    laminate: context.t("Laminate"),
    cutting: context.t("Cutting"),
    material: context.t("Material"),
    color: context.t("Color"),
    border: context.t("Border"),
    underglass: context.t("Underglass"),
    filecheck: context.t("File check"),
    lines: context.t("Lines"),
    glossy: context.t("GLOSSY"),
    matt: context.t("MATT"),
    neon: context.t("NEON"),
    frosted: context.t("FROSTED"),
    none: context.t("none"),
    rectangular: context.t("rectangular"),
    outline: context.t("outline"),
    exempted: context.t("exempted"),
    whiteprint_partial: context.t(
      "Partial white printing (CMYK + spot color white)"
    ),
    whiteprint_full: context.t("Full white print (CMYK + spot color white)"),
    whiteprint_onlywhite: context.t("White print only (special color white)"),
    whiteprint_white_ink_underlay: context.t("White Ink Underlay"),
    whiteprint_contoured_white_backfill: context.t("Contoured White Backfill"),
    whiteprint_white_layer_custom_print: context.t("White Layer Custom Print"),
    stripwidth: context.t("Width of Strips: 70cm (max)"),
    skip_file_upload: context.t("File Upload after Order"),
    missingSection: context.t(
      "Missing section: Please contact customer service: kontakt@myfolie.com - "
    ),
  };
  Object.keys(FILEEDITORTYPES).forEach((name) => {
    translations[name] = context.t(`${name}.title`);
  });

  // Old Use Effect Stuff
  useEffect(() => {
    if (!sku && file) {
      dispatch(resetFileUpload());
      setFileSizeModal(false);
    }
  }, [sku]);

  useEffect(() => {
    if (!pristine) DataLayerHelper.addEvent("file_editor_start", "file");
  }, [pristine]);

  // Guard: If EditorType or some Value changed, check if they match the editor configuration

  useEffect(() => {
    const editorConfig = FILEEDITOR?.[editorType] || {};
    const {
      availableSizes = [],
      discounts = [],
      sections = [],
      forceCutting,
      showDefaultAmounts,
      cropShape,
    } = editorConfig;

    // If Configurator Type has section sizeselect, only available size can be choosen. If size is not available, set it to first available size.
    const handleSizeSelection = () => {
      if (sections.includes("sizeselect")) {
        const sizeExists = availableSizes.some((sizeGroup) =>
          sizeGroup.sizes.some(
            ([availableWidth, availableHeight]) =>
              availableWidth === width && availableHeight === height
          )
        );

        if (!sizeExists) {
          const [defaultWidth, defaultHeight] = availableSizes[0]?.sizes[0];
          changeSelectedSize(defaultWidth, defaultHeight);
        }
      }
    };

    // If Configuration Type enforces a cutting type we change the cutting type
    const handleCuttingEnforcement = () => {
      if (forceCutting && cutting !== forceCutting) {
        setValue("cutting", forceCutting);
      }
    };

    // If Configurator Type has showDefaultAmounts means they are only sold in specific amounts. On editor change we need to enforce that it matches the amount
    const handleDefaultAmounts = () => {
      if (showDefaultAmounts) {
        const amountExists = discounts.some(
          (discount) => discount.amount === amount
        );
        if (!amountExists) {
          setValue("amount", parseInt(discounts[0]?.amount || "1", 10));
        }
      }
    };

    // If Configurator Type has a cropShape we need to enforce cropping (e.g. Circular Stickers)
    const handleCropping = () => {
      if (cropShape) {
        dispatch(startCrop());
      }
    };

    // If Size of the File is very different from the selected size we need to show a warning and ask the user for cropping
    const handleSizeMismatchModal = () => {
      const aspectRatioMismatch =
        Math.abs(
          width / height -
            artwork?.data?.attributes?.width / artwork?.data?.attributes?.height
        ) > 0.001;

      if (
        artwork?.data?.attributes?.width &&
        !fileSizeModal &&
        !sizeChangeActive &&
        !handledFileSize &&
        !cropping?.active &&
        sizeChanged &&
        aspectRatioMismatch
      ) {
        setFileSizeModal(true);
        DataLayerHelper.addEventWithOrigin(
          "error",
          "file editor",
          "size mismatch",
          "open modal"
        );
      }
    };

    // If the filetype is not a valid filetype for vectors but the cutting requires vectory we need to notify the user
    const handleVectorizeModal = () => {
      const validFileTypes = [
        "application/postscript",
        "image/svg+xml",
        "application/pdf",
      ];
      if (
        ["outline", "exempted"].includes(cutting) &&
        !handledVectorizeFile &&
        artwork?.data?.attributes?.preview &&
        !validFileTypes.includes(artwork?.data?.attributes?.filetype)
      ) {
        setVectorizeFileModal(true);
        DataLayerHelper.addEventWithOrigin(
          "error",
          "file editor",
          "vectorization",
          "open modal"
        );
      }
    };

    // If color is not available in current materialSerie make sure it is
    // Hint: Do not add color to use effect otherwise it is a endless loop
    const handleMaterialTypeChange = () => {
      if (sections.includes("color") && materialSerie) {
        const matchingOption =
          COLORS[materialSerie].find(
            (option) => option?.title === color?.title
          ) || COLORS[materialSerie][0];

        if (JSON.stringify(matchingOption) !== JSON.stringify(color?.value)) {
          setValue(`color`, matchingOption);
        }
      }
    };

    // Modularized calls
    handleSizeSelection();
    handleCuttingEnforcement();
    handleDefaultAmounts();
    handleCropping();
    handleSizeMismatchModal();
    handleVectorizeModal();
    handleMaterialTypeChange();
  }, [
    editorType,
    materialSerie,
    cutting,
    file,
    height,
    width,
    keepRatio,
    fileRatio,
    vectorizeFile,
  ]);

  // Helper Functions
  const handleRemoveCrop = () => {
    if (artwork?.data?.attributes?.width) {
      const optimized_sizes = calculateOptimizedSizeProposal(
        artwork?.data?.attributes,
        width,
        height
      );
      setValue("width", optimized_sizes.width);
      setValue("height", optimized_sizes.height);
      setValue("fileRatio", optimized_sizes.ratio);
      setValue("keepRatio", true);
    }
    if (cropping?.active) {
      DataLayerHelper.addEventWithOrigin(
        "behaviour",
        "file editor",
        "cropping",
        "reset"
      );
      dispatch(endCrop());
    }
  };

  const changeSizeChangeActive = (sizeChangeActiveValue, origin) => {
    setSizeChangeActive(sizeChangeActiveValue);
    if (
      !keepRatio &&
      artwork?.data?.attributes?.filetype &&
      !cropping?.active &&
      !fileSizeModal &&
      !sizeChangeActiveValue &&
      sizeChanged &&
      (Math.round((width / height) * 10) !==
        Math.round(
          (artwork?.data?.attributes?.width /
            artwork?.data?.attributes?.height) *
            10
        ) ||
        width / height -
          artwork?.data?.attributes?.width / artwork?.data?.attributes?.height >
          0.001)
    ) {
      toggleFileSizeModal();
      DataLayerHelper.addEventWithOrigin(
        "error",
        "file editor",
        "size missmatch",
        "open modal"
      );
    }
  };

  // New Artwork Handling
  const [artworkFileData, setArtworkFileData] = useState();

  useEffect(() => {
    if (!artwork || !artwork.data?.id) {
      return;
    }

    // Check if all attributes are set before opening SSE
    if (
      artwork.data.attributes?.preview &&
      ((artwork.data.attributes?.width && artwork.data.attributes?.height) ||
        artwork.data.attributes?.analyzed_at)
    ) {
      return;
    }

    if (artwork.data.attributes?.surrender) {
      alert(
        "Something went wrong generating your Preview Image. Please reload the Page and order without direct upload."
      );
      return;
    }

    const evtSource = new EventSource(
      `${REACT_APP_COP_SERVER}/api/v1/artworks/status_stream?id=${artwork.data.id}`
    );

    evtSource.onmessage = (event) => {
      console.log("AW Event", event);

      if (event.data) {
        const updatedArtwork = JSON.parse(event.data);
        dispatch(uploadSuccess(updatedArtwork));

        // Check if all attributes are now set
        if (
          updatedArtwork?.data?.attributes?.width &&
          updatedArtwork?.data?.attributes?.height
        ) {
          if (
            updatedArtwork?.data?.attributes?.width &&
            updatedArtwork?.data?.attributes?.height &&
            !sizeChanged
          ) {
            const scaledWidth = Math.min(
              Math.round(updatedArtwork?.data?.attributes?.width / 3),
              1500
            );
            const scaledheight = Math.min(
              Math.round(updatedArtwork?.data?.attributes?.height / 3),
              1500
            );

            if (scaledWidth > 1500 || scaledheight > 1500) {
              if (scaledWidth > scaledheight) {
                setValue("width", 1500);
                setValue("height", 1500 * (scaledheight / scaledWidth));
              } else {
                setValue("width", 1500 * (scaledheight / scaledWidth));
                setValue("height", 1500);
              }
            } else {
              setValue("width", scaledWidth);
              setValue("height", scaledheight);
            }
            setValue("fileRatio", scaledheight / scaledWidth);
            setValue("keepRatio", true);
          }
        }
        if (
          updatedArtwork?.data?.attributes?.preview &&
          ((updatedArtwork?.data?.attributes?.width &&
            updatedArtwork?.data?.attributes?.height) ||
            updatedArtwork.data.attributes?.analyzed_at)
        ) {
          evtSource.close(); // Close the connection
        }
      }
    };

    evtSource.onerror = (error) => {
      console.error("EventSource error:", error);
      evtSource.close(); // Close on error
    };

    // Cleanup on unmount
    return () => {
      evtSource.close();
      console.log("EventSource closed on cleanup.");
    };
  }, [artwork, dispatch]);

  const handleModalFileUpload = (e) => {
    e.preventDefault();
    dispatch(
      uploadFile({
        files: e.target.files,
      })
    );

    DataLayerHelper.addEventWithOrigin(
      "behaviour",
      "file editor",
      vectorizeFileModal ? "vectorization" : "size missmatch",
      "upload different file"
    );

    setFileSizeModal(false);
    setVectorizeFileModal(false);
  };

  useEffect(() => {
    if (currentArtworkId) setValue("artworkId", currentArtworkId);
  }, [currentArtworkId]);

  // Start Cropping
  const handleCropFile = () => {
    dispatch(startCrop());

    setFileSizeModal(!fileSizeModal);

    DataLayerHelper.addEventWithOrigin(
      "behaviour",
      "file editor",
      "size missmatch",
      "start cropping"
    );
  };

  // Cart Modal Handling
  const toggleAddToCartErrorModal = () => {
    if (addToCartError) {
      dispatch(resetCart());
      resetCookieError();
    } else {
      setAddToCartErrorModal(!addToCartErrorModal);
    }
  };

  const closeAddToCartWarning = () => {
    setAddToCartWarningModal(false);
  };

  const skipAddToCartWarning = () => {
    closeAddToCartWarning();
    onSubmit();
  };

  const toggleFileSizeModal = () => {
    // check if it should open... and open if needed
    // if (keepRatio + width/height != ratio + !sizeChangeActiveState + ) {

    setFileSizeModal(!fileSizeModal);
  };

  const toggleSizeSelectModal = () => {
    setSizeSelectModalIsOpen(!sizeSelectModalIsOpen);
  };

  const toggleVectorizeFileModal = () => {
    setVectorizeFileModal(!vectorizeFileModal);
    setHandledVectorizeFile(true);
  };

  const useUnvectorizedFileAndCloseModal = () => {
    setVectorizeFileModal(false);
    setHandledVectorizeFile(true);
    const filetype = artwork?.data?.attributes?.filetype || "UNKOWN";
    DataLayerHelper.addEventWithOrigin(
      "behaviour",
      "file editor",
      "vectorization",
      "use existing file (" + filetype + ")"
    );
  };

  const togglePanel = (panelName) => {
    setOpen(open !== panelName ? panelName : "");
  };

  // TODO maybe SKU should come from backend now?
  const handleUpdateSku = () => {
    let newSku = sku;
    // if sku does not exist generate one and use it
    if (!sku) {
      newSku = EditorFormHelpers.generateSKU("designer");
      setValue("sku", EditorFormHelpers.generateSKU("designer"));
    }
    return newSku;
  };

  // TODO unsued, check if the state was needed
  const handleSkipFileUploadChange = (e) => {
    setSkipFileUploadTouched(true);
  };

  // TODO is only used if editor was changed
  const changeSelectedSize = (width, height) => {
    setValue("width", width);
    setValue("height", height);
    setValue("fileRatio", height / width);

    if (!sizeChanged) {
      setSizeChanged(true);
    }

    setSizeSelectModalIsOpen(false);

    // TODO if ratio missmatch start cropping
    if (
      artwork?.data?.attributes?.filetype &&
      !cropping?.active &&
      (Math.round((width / height) * 10) !==
        Math.round(
          (artwork?.data?.attributes?.width /
            artwork?.data?.attributes?.height) *
            10
        ) ||
        width / height -
          artwork?.data?.attributes?.width / artwork?.data?.attributes?.height >
          0.001)
    ) {
      toggleFileSizeModal();
    }
  };

  // TODO We need ot check if Sizes, Laminates and Other Options work
  const handleFileEditorTypeChange = (e, newType, oldType) => {};

  const handleBookVectorization = () => {
    setValue("vectorizeFile", true);
    toggleVectorizeFileModal();
    DataLayerHelper.addEventWithOrigin(
      "behaviour",
      "file editor",
      "vectorization",
      "add vectorization service"
    );
  };

  const getBorderMax = () => {
    let maxBorderWidth = 30;
    if (cutting === "exempted") {
      maxBorderWidth = 0;
    }
    return maxBorderWidth;
  };

  const calculateDPI = () => {
    let dpi = 0;

    if (artwork?.data?.attributes?.filetype) {
      const vectorFiletypes = [
        "application/postscript",
        "image/svg+xml",
        "application/pdf",
      ];

      if (vectorFiletypes.includes(artwork.data.attributes.filetype)) {
        dpi = 1200;
      } else if (artwork?.data?.attributes?.width && width && width > 0) {
        dpi = Math.round((artwork.data.attributes.width * 25.4) / width);
      }
    }

    if (artwork?.data?.attributes?.dpi) {
      dpi = Math.round(
        artwork?.data?.attributes?.dpi *
          (artwork.data.attributes.width / 3 / width)
      );
    }
    dpi = Math.min(dpi, 1200); // Cap at 1200
    return dpi || 300; // Default DPI if no conditions match
  };

  const getStarColor = (dpi) => {
    const qualityGood = 225;
    const qualityMedium = 165;
    let colorClass = "";
    if (dpi >= qualityGood) {
      colorClass = "success";
    } else if (dpi >= qualityMedium) {
      colorClass = "warning";
    } else if (dpi < qualityMedium) {
      colorClass = "danger";
    }
    return colorClass;
  };

  const showDPIStars = (dpi) => {
    return false;
    if (dpi) {
      const maxDPI = 150;
      const starElements = [];
      if (dpi >= maxDPI) {
        starElements.push(<FaStar key={`star_1`} aria-hidden="true" />);
        starElements.push(<FaStar key={`star_2`} aria-hidden="true" />);
        starElements.push(<FaStar key={`star_3`} aria-hidden="true" />);
        starElements.push(<FaStar key={`star_4`} aria-hidden="true" />);
        starElements.push(<FaStar key={`star_5`} aria-hidden="true" />);
      } else {
        let stars = Math.round((dpi / 60) * 10) / 10;
        let starIdx = 0;
        while (stars > 0) {
          starIdx++;
          if (stars > 1 || stars % 1 > 0.75 || stars % 1 === 0) {
            starElements.push(
              <FaStar key={`star_${starIdx}`} aria-hidden="true" />
            );
          } else {
            starElements.push(
              <FaStarHalf key={`star_${starIdx}`} aria-hidden="true" />
            );
          }
          stars--;
        }
      }
      return starElements;
    }
    return false;
  };

  // Function to create or save project and add to card. tbd save as draft
  const onSubmit = (values) => {
    const json = EditorFormHelpers.generateJson(
      formState,
      language,
      translations,
      artwork,
      sku,
      cropping
    );

    const parsedEditorType = parseInt(editorType);
    const availableSizes =
      FILEEDITOR && parsedEditorType && FILEEDITOR[parsedEditorType]
        ? FILEEDITOR[parsedEditorType].availableSizes
        : [];
    const sections =
      FILEEDITOR && parsedEditorType && FILEEDITOR[parsedEditorType]
        ? FILEEDITOR[parsedEditorType].sections
        : [];

    if (isNaN(json.amount)) {
      // TODO handle here case when number inputs hold text. Just on IE
      const message = context.t("height, Width and Amount must be numbers!");
      let alertOptions = {
        message,
        color: "danger",
        origin: "text editor form submit",
        timeout: 5,
      };
      dispatch(notificationsActions.showAlert(alertOptions));
    } else if (
      isNaN(json.width) ||
      isNaN(json.height) ||
      json.width < 10 ||
      json.height < 10
    ) {
      toggleAddToCartErrorModal();
      DataLayerHelper.addEventWithOrigin(
        "error",
        "file editor",
        "onSubmit",
        "sizes to small"
      );
    } else if (!artwork?.data?.attributes?.width && !skip_file_upload) {
      toggleAddToCartErrorModal();
      DataLayerHelper.addEventWithOrigin(
        "error",
        "file editor",
        "onSubmit",
        "file missing"
      );
    } else if (
      sections.includes("sizeselect") &&
      !availableSizes.find((availableSizeObj) => {
        return availableSizeObj.sizes.find((availableSize) => {
          return (
            availableSize[0] === json.width && availableSize[1] === json.height
          );
        });
      })
    ) {
      toggleAddToCartErrorModal();
      DataLayerHelper.addEventWithOrigin(
        "error",
        "file editor",
        "onSubmit",
        "unavailable size"
      );
    } else if (
      FILEEDITOR[parsedEditorType]?.showDefaultAmounts &&
      json.amount < FILEEDITOR[parsedEditorType]?.discounts[0].amount
    ) {
      toggleAddToCartErrorModal();
      DataLayerHelper.addEventWithOrigin(
        "error",
        "file editor",
        "onSubmit",
        "minimum amount unmatched"
      );
    } else {
      dispatch(
        createProject({
          project: {
            uid: json.sku,
            artwork_id: artworkId,
            ...json,
            product_variant_id: FILEEDITOR[parsedEditorType].variantId,
            product_type:
              parsedEditorType === 2 ? "custom_plott" : "digital_print",
            production_method: [2, 6].includes(parsedEditorType)
              ? "plotting"
              : "printing",
            material_id: FILEEDITOR[parsedEditorType].material_id,
            source: "multi",
          },
          successCallback: (payload) => {
            dispatch(
              addItemToCart({
                variant_id: FILEEDITOR[parsedEditorType].variantId,
                quantity: json.amount,
                options: {
                  project_uid: json.sku,
                  project_meta: JSON.stringify({
                    pid: payload?.data?.id,
                    artwork_id: artworkId,
                    title: json.productTitle,
                    description: json.description,
                    width: json.width,
                    height: json.height,
                    thumbnail: skip_file_upload
                      ? false
                      : payload?.data?.attributes?.proof_preview
                      ? REACT_APP_COP_SERVER +
                        payload?.data?.attributes?.proof_preview
                      : json.preview,
                    uid: json.sku,
                    cutting: json.cutting,
                    artwork_id: artworkId,
                    filecheck: json.filecheck,
                    vectorizeFile: json.vectorizeFile,
                    price: json.price,
                    priceBeforeDiscountAndTax: json.priceBeforeDiscountAndTax,
                    skip_file_upload: skip_file_upload,
                    editor: "v3",
                    source: "multi",
                  }),
                },
              })
            );
          },
        })
      );
    }
  };

  // there is a better way to do this
  const renderFormErrors = () => {
    const parsedEditorType = parseInt(editorType);
    const availableSizes =
      FILEEDITOR && parsedEditorType && FILEEDITOR[parsedEditorType]
        ? FILEEDITOR[parsedEditorType].availableSizes
        : [];
    const sections =
      FILEEDITOR && parsedEditorType && FILEEDITOR[parsedEditorType]
        ? FILEEDITOR[parsedEditorType].sections
        : [];
    if (sections.includes("sizeselect")) {
      if (
        !availableSizes.find((availableSizeObj) => {
          return availableSizeObj.sizes.find((availableSize) => {
            return availableSize[0] === width && availableSize[1] === height;
          });
        })
      ) {
        return context.t(
          "Please select a different size. Unfortunately this product can only be produced in our predefined sizes."
        );
      }
    }
    if (
      FILEEDITOR &&
      parsedEditorType &&
      FILEEDITOR[parsedEditorType]?.showDefaultAmounts
    ) {
      if (amount < FILEEDITOR[parsedEditorType]?.discounts[0].amount) {
        return (
          context.t("Minimum order amount for this product is") +
          " " +
          FILEEDITOR[parsedEditorType]?.discounts[0].amount
        );
      }
    }
    if (!artwork?.data?.attributes?.widthFile && !skip_file_upload)
      return context.t("You need to upload a file first");
    if (isNaN(width) || isNaN(height) || width < 10 || height < 10)
      return context.t("Minimum size for height and width is 10mm = 0.1cm");
    if (cookieError && addToCartError)
      return (
        context.t("An error occurred while adding your data to the cart. ") +
        context.t(
          "Cookies are disabled in your browser. Please enable mandatory cookies to add an item to your cart."
        )
      );
    if (!cookieError && addToCartError)
      return context.t(
        "An error occurred while adding your data to the cart. Please try again later"
      );
    return context.t(
      "We are sorry! An unkown error occured. Please try again."
    );
  };
  // there is a better way to do this
  const renderFormWarnings = () => {
    const parsedEditorType = parseInt(editorType);
    const defaultWarningSize = 120;

    if (!width || !height) {
      return (
        <>
          <p>
            {context.t("Minimum size for height and width is 10mm = 0.1cm")}
          </p>
        </>
      );
    } else if (
      (width <
        (FILEEDITOR && parsedEditorType && FILEEDITOR[parsedEditorType]
          ? FILEEDITOR[parsedEditorType].warningSize || defaultWarningSize
          : defaultWarningSize) &&
        height <
          (FILEEDITOR && parsedEditorType && FILEEDITOR[parsedEditorType]
            ? FILEEDITOR[parsedEditorType].warningSize || defaultWarningSize
            : defaultWarningSize)) ||
      width <
        (FILEEDITOR && parsedEditorType && FILEEDITOR[parsedEditorType]
          ? FILEEDITOR[parsedEditorType].warningSize || defaultWarningSize
          : defaultWarningSize) /
          2 ||
      height <
        (FILEEDITOR && parsedEditorType && FILEEDITOR[parsedEditorType]
          ? FILEEDITOR[parsedEditorType].warningSize || defaultWarningSize
          : defaultWarningSize) /
          2
    )
      return (
        <>
          <p>
            {context.t(
              "The size of your design looks very small. Please double check you did not confuse MM with CM. Your selected measurements are:"
            )}
            <br />
            <br />
            <strong>{context.t("Size of your vinyl (width x height):")}</strong>
            <br />
            <span className="mr-1">
              <strong>
                <FormattedNumber
                  value={Math.round(width * 100) / 100}
                  language={language}
                  unit="mm"
                  decimals={1}
                />
              </strong>
              &nbsp;=&nbsp;
              <FormattedNumber
                value={Math.round(width * 10) / 100}
                language={language}
                unit="cm"
                decimals={2}
              />
            </span>
            x
            <span className="ml-1">
              <strong>
                <FormattedNumber
                  value={Math.round(height * 100) / 100}
                  language={language}
                  unit="mm"
                  decimals={1}
                />
              </strong>
              &nbsp;=&nbsp;
              <FormattedNumber
                value={Math.round(height * 10) / 100}
                language={language}
                unit="cm"
                decimals={2}
              />
            </span>
          </p>
        </>
      );
  };

  // reset the form
  const resetForm = (skipResetCart) => {
    reset({
      editorType: initialValues.editorType,
      width: initialValues.width,
      height: initialValues.height,
      ...initialValues,
    });

    dispatch(resetFileUpload());
    // reset custom state

    setOpen("size");
    setSizeChanged(initialValues.isSizePresetted);
    setFileSizeModal(false);
    setHandledFileSize(false);
    setVectorizeFileModal(false);
    setHandledVectorizeFile(false);
    setAddToCartErrorModal(false);
    setAddToCartWarningModal(false);
    setSkipFileUploadTouched(false);
    setSizeChangeActive(false);

    if (!skipResetCart) {
      dispatch(resetCart());
      DataLayerHelper.addEventWithOrigin(
        "behaviour",
        "file editor",
        "resetForm"
      );
    }
  };

  const formState = watch();

  // definitions that contain too much logic or are uneccesarry
  const calculatedPrice = EditorFormHelpers.calculatePrice(formState);
  const selectedMaterials = Object.keys(COLORTYPES).filter(
    (key) => COLORTYPES[key] === parseInt(materialSerie)
  );
  let selectedMaterial = "GLOSSY";
  if (selectedMaterials.length > 0) selectedMaterial = selectedMaterials[0];
  const parsedEditorType = parseInt(editorType);

  const availableSizes =
    FILEEDITOR && parsedEditorType && FILEEDITOR[parsedEditorType]
      ? FILEEDITOR[parsedEditorType].availableSizes
      : [];
  const sections =
    FILEEDITOR && parsedEditorType && FILEEDITOR[parsedEditorType]
      ? FILEEDITOR[parsedEditorType].sections
      : [];
  const laminates =
    FILEEDITOR && parsedEditorType && FILEEDITOR[parsedEditorType]
      ? FILEEDITOR[parsedEditorType].laminates
      : [];
  const cuttings =
    FILEEDITOR && parsedEditorType && FILEEDITOR[parsedEditorType]
      ? FILEEDITOR[parsedEditorType].cuttings
      : [];
  const dpi = calculateDPI();
  const starColor = getStarColor(dpi);
  const dpiStars = showDPIStars(dpi);

  const showSmallSizeWarning =
    isNaN(height) || isNaN(width) || height < 10 || width < 10;

  const showMightBeDividedHint = (() => {
    const boundaries = FILEEDITOR?.[parsedEditorType]?.boundaries?.maxNoDevide;
    return boundaries && height > boundaries && width > boundaries;
  })();

  const smallDevice = !isServer
    ? window.matchMedia("(max-width: 767px)").matches
    : false;
  const showSkipFileSection =
    ((skip_file_upload || !skipFileUploadTouched) &&
      !artwork?.data?.attributes?.filetype &&
      uploadProgress === 0) ||
    (skip_file_upload &&
      artwork?.data?.attributes?.filetype &&
      uploadProgress === 0);
  const displayPriceAmount =
    FILEEDITOR[parsedEditorType] &&
    FILEEDITOR[parsedEditorType]?.showDefaultAmounts
      ? parseInt(amount)
      : 1;

  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(onSubmit)}>
        <Container className="fileeditor px-0" fluid>
          <Row className="fileeditor__row no-gutters">
            <Col md={{ size: 4 }} className="fileeditor__sidebar">
              <div className="p-3 fileeditor__sidebar__heading">
                <h1 className="h5 mb-0">
                  {context.t("Configure your custom Vinyl, Sticker and more")}
                </h1>
              </div>

              <ProductSelectionSection
                togglePanel={togglePanel}
                isOpen={true}
                language={language}
                handleFileEditorTypeChange={handleFileEditorTypeChange}
              />

              {sections.map((sectionType) => {
                switch (sectionType) {
                  case "size":
                    return (
                      <SizeSection
                        key={sectionType}
                        changeSizeChangeActive={changeSizeChangeActive}
                        handleRemoveCrop={handleRemoveCrop}
                        togglePanel={togglePanel}
                        isOpen={open === sectionType}
                        showMightBeDividedHint={showMightBeDividedHint}
                        showSmallSizeWarning={showSmallSizeWarning}
                      />
                    );
                  case "sizeselect":
                    return (
                      <SizeSelectSection
                        key={sectionType}
                        changeSizeChangeActive={changeSizeChangeActive}
                        availableSizes={availableSizes}
                        togglePanel={togglePanel}
                        displayPriceAmount={displayPriceAmount}
                        showDefaultAmounts={
                          FILEEDITOR[parsedEditorType]?.showDefaultAmounts
                        }
                        isOpen={open === "size"}
                      />
                    );
                  case "laminate":
                    return (
                      <LaminateSection
                        key={sectionType}
                        togglePanel={togglePanel}
                        isOpen={open === sectionType}
                        laminates={laminates}
                      />
                    );
                  case "cutting":
                    return (
                      <CuttingSection
                        key={sectionType}
                        togglePanel={togglePanel}
                        isOpen={open === sectionType}
                        cuttings={cuttings}
                      />
                    );
                  case "whiteprint_mode":
                    return (
                      <WhiteprintModeSection
                        key={sectionType}
                        togglePanel={togglePanel}
                        isOpen={open === sectionType}
                      />
                    );
                  case "vinyl":
                    return (
                      <VinylSection
                        key={sectionType}
                        togglePanel={togglePanel}
                        isOpen={open === sectionType}
                      />
                    );
                  case "color":
                    return (
                      <ColorSection
                        key={sectionType}
                        togglePanel={togglePanel}
                        isOpen={open === sectionType}
                      />
                    );
                  case "border":
                  case "filecheck":
                  case "underglass":
                  case "stripwidth":
                    return null;
                  default:
                    return (
                      <div className="alert alert-danger text-bold">
                        ⚠️ Section Type {sectionType} Missing. Please contact
                        support!
                      </div>
                    );
                }
              })}

              {(sections.includes("underglass") ||
                sections.includes("border")) && (
                <AdditionalOptionsSection
                  togglePanel={togglePanel}
                  isOpen={open === "extra"}
                  extraOptions={{
                    underglass: sections.includes("underglass"),
                    border: sections.includes("border"),
                  }}
                  getBorderMax={getBorderMax}
                />
              )}

              {smallDevice && (
                <EditorFormSection
                  panelName={"fileUpload"}
                  panelTitle={`${context.t("Upload File")} & ${context.t(
                    "Preview"
                  )}`}
                  open={open === "fileUpload"}
                  toggle={togglePanel}
                >
                  <>
                    <h2 className="h6">
                      {context.t("Your custom design file")}
                    </h2>
                    <Row>
                      <Col
                        md={{ size: showSkipFileSection ? 12 : 12 }}
                        className={`${showSkipFileSection ? "" : "bg-white"}`}
                      >
                        {showSkipFileSection && (
                          <>
                            <h3 className="h5">
                              {context.t("Upload File later")}
                            </h3>
                            <h3 className="text-remark">
                              {context.t("Proceed without file upload")}
                            </h3>
                            <p>
                              <strong>
                                {context.t(
                                  "For Designers and Business Customers."
                                )}
                              </strong>
                              <br />
                              {context.t(
                                "If you are a designer or business customer you can also upload your file later. Please make sure that your file is created with the correct file dimensions and follows our design guidelines. You need to provide your file later as EPS, AI or PDF file. We are going to send you instructions by email for the fileupload after you completed your order."
                              )}
                              {context.t(
                                "Please read carefully our Guidelines and provide files according to it:"
                              )}{" "}
                              <a
                                href={`/${language}/fileguide/`}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                <img
                                  src={fileguideIcon}
                                  alt={context.t("Production Data Guidelines")}
                                  height="20"
                                />{" "}
                                {context.t("Production Data Guidelines")}
                              </a>
                            </p>
                            <Label className="ml-3">
                              <Controller
                                name="skip_file_upload"
                                control={control}
                                render={({ field }) => (
                                  <Input
                                    type={"checkbox"}
                                    checked={field.value}
                                    {...field}
                                  />
                                )}
                              />
                              {context.t(
                                "I can provide Design Files and want to upload the files later."
                              )}
                            </Label>
                          </>
                        )}
                      </Col>
                      <Col
                        md={{ size: showSkipFileSection ? 12 : 12 }}
                        style={skip_file_upload ? { opacity: "0.5" } : {}}
                      >
                        {showSkipFileSection && (
                          <h3 className="h5">{context.t("Upload file now")}</h3>
                        )}
                        <h3 className="text-remark">{context.t("Preview")}</h3>

                        <FileUploadPreview
                          cropShape={
                            FILEEDITOR[parsedEditorType]
                              ? FILEEDITOR[parsedEditorType].cropShape
                              : false
                          }
                          allowedFormats={allowedFormats}
                          uploadFile={(args) => {
                            dispatch(uploadFile(args));
                          }}
                          handleUpdateSku={handleUpdateSku}
                          file={artwork?.data?.attributes}
                          forceWhiteBackground={
                            cutting === "rectangular" &&
                            parsedEditorType !==
                              FILEEDITORTYPES.STICKER_WHITEPRINT
                          }
                          formRatio={width / height}
                          sku={sku}
                          sizeChanged={sizeChanged}
                          acceptedMIMETypes={
                            "image/gif, image/jpeg, image/pjpeg, image/png, application/octet-stream, application/postscript, application/pdf, image/svg+xml, image/tiff, image/tiff-fx, image/vnd.adobe.photoshop, application/x-photoshop, application/photoshop, application/vnd.adobe.photoshop, application/psd, image/psd, image/*, .psd"
                          }
                        />
                      </Col>
                    </Row>
                    {artwork?.data?.attributes?.width &&
                      !sections.includes("color") &&
                      (cutting === "rectangular" ||
                        cutting === "outline" ||
                        cutting === "exempted") && (
                        <div className="d-flex bg-white pt-1 px-2">
                          <em className="text-remark">
                            *
                            {parsedEditorType !==
                              FILEEDITORTYPES.STICKER_WHITEPRINT && (
                              <>
                                {context.t(
                                  "Hint: We print your design on white vinyl."
                                )}
                                &nbsp;
                              </>
                            )}
                            {parsedEditorType ===
                              FILEEDITORTYPES.STICKER_WHITEPRINT && (
                              <>
                                {context.t(
                                  "Hint: We print your design on transparent vinyl."
                                )}
                                &nbsp;
                              </>
                            )}
                            {cutting === "outline" &&
                              context.t(
                                "The preview does not represent the actual outline. Please also read the hint on the cutting selection."
                              )}
                          </em>
                        </div>
                      )}
                    <div className="d-flex bg-white p-3">
                      <img
                        src={instructions}
                        alt="size_instructions"
                        height="90px"
                        loading="lazy"
                      />
                      <div className="flex-fill">
                        <div>
                          <span className="small">
                            {context.t("Size of your vinyl (width x height):")}
                          </span>
                          <br />
                          <strong>
                            <FormattedNumber
                              value={Math.round(width * 100) / 100}
                              language={language}
                              unit="mm"
                              decimals={1}
                            />
                            &nbsp;x&nbsp;
                            <FormattedNumber
                              value={Math.round(height * 100) / 100}
                              language={language}
                              unit="mm"
                              decimals={1}
                            />
                            &nbsp;=<span> </span>
                            <FormattedNumber
                              value={Math.round(width * 10) / 100}
                              language={language}
                              unit="cm"
                              decimals={2}
                            />
                            &nbsp;x&nbsp;
                            <FormattedNumber
                              value={Math.round(height * 10) / 100}
                              language={language}
                              unit="cm"
                              decimals={2}
                            />
                          </strong>
                          {showSmallSizeWarning && (
                            <p className="small text-danger">
                              ⚠️{" "}
                              {context.t(
                                "The minimum size for height and width is 10mm "
                              )}
                            </p>
                          )}
                          {showMightBeDividedHint && (
                            <p className="small text-danger">
                              ℹ️{" "}
                              {context.t(
                                "For technical reasons, your design may need to be divided into several parts, as both dimensions exceed the maximum manufacturable size. It can be installed without any visible gap."
                              )}
                            </p>
                          )}
                        </div>
                        {dpiStars && (
                          <Row>
                            <Col xs={5}>
                              {context.t("Quality at this size:")}
                            </Col>
                            <Col xs={7} className={"text-" + starColor}>
                              {dpiStars} &nbsp; (
                              <FormattedNumber
                                value={Math.round(dpi * 100) / 100}
                                language={language}
                                decimals={1}
                              />
                              &nbsp;{context.t("DPI")})
                            </Col>
                          </Row>
                        )}
                      </div>
                    </div>
                  </>
                </EditorFormSection>
              )}

              <FileCheckSection
                togglePanel={togglePanel}
                isOpen={open === "filecheck"}
              />

              <SummarySection
                togglePanel={togglePanel}
                isOpen={open === "summary"}
                sections={sections}
              />

              {FILEEDITOR[parsedEditorType]?.discounts && (
                <EditorDiscountTable
                  language={language}
                  discounts={FILEEDITOR[parsedEditorType].discounts}
                />
              )}

              <div className="fileeditor__sidebar__submit">
                <div className="d-flex align-items-center justify-content-between">
                  {FILEEDITOR[parsedEditorType]?.showDefaultAmounts && (
                    <div
                      className="fileeditor__sidebar__submit__amount pl-1"
                      style={{ width: "28%" }}
                    >
                      <Controller
                        name="amount"
                        control={control}
                        rules={{ min: 1 }}
                        render={({ field }) => (
                          <AmountSelectField
                            {...field}
                            language={language}
                            discounts={FILEEDITOR[parsedEditorType].discounts}
                          />
                        )}
                      />
                    </div>
                  )}

                  {!FILEEDITOR[parsedEditorType]?.showDefaultAmounts && (
                    <>
                      <div className="fileeditor__sidebar__submit__amount pl-3">
                        <FormGroup>
                          <Controller
                            name="amount"
                            control={control}
                            render={({ field }) => (
                              <Input
                                min="1"
                                placeholder={context.t("amount")}
                                style={{ textAlign: "center" }}
                                className="mr-1 no-spin-buttons data-hj-allow"
                                type="number"
                                step="1"
                                {...field}
                              />
                            )}
                          />
                        </FormGroup>
                      </div>
                      <div className="fileeditor__sidebar__submit__times text-center">
                        <FaTimes className="fa-lg fa-fw text-white" />
                      </div>
                    </>
                  )}

                  <div className="fileeditor__sidebar__submit__button">
                    <Button
                      color="primary"
                      block
                      type="button"
                      disabled={addingToCart}
                      onClick={
                        renderFormWarnings()
                          ? () => setAddToCartWarningModal(true)
                          : methods.handleSubmit(onSubmit)
                      }
                    >
                      <div className="d-flex flex-column flex-lg-row justify-content-between">
                        <strong>
                          <FormattedNumber
                            value={parseFloat(
                              calculatedPrice.displayPrice * displayPriceAmount
                            )}
                            language={language}
                            unit="€"
                            decimals={2}
                          />
                        </strong>
                        <strong>
                          {context.t("Add to Cart")}
                          <FaChevronRight className="fa-fw" />
                        </strong>
                      </div>
                    </Button>

                    <Modal
                      fade={false}
                      isOpen={!!projectCreateError || !!artworkUploadError}
                      toggle={() => {
                        dispatch(resetCreateFail());
                        dispatch(resetUploadFail());
                      }}
                      centered
                    >
                      <ModalHeader
                        toggle={() => {
                          dispatch(resetCreateFail());
                          dispatch(resetUploadFail());
                        }}
                      >
                        {context.t("Alert")}
                      </ModalHeader>
                      <ModalBody>
                        <div>
                          <div className="d-flex justify-content-center mb-3">
                            <FaTimes className="fa-fw fa-5x text-danger" />
                          </div>
                          <p>
                            {projectCreateError &&
                              context.t(
                                "We couldn't save your configuration at this time."
                              )}
                            {artworkUploadError &&
                              context.t(
                                "We couldn't upload your file at this time. "
                              )}{" "}
                            {context.t(
                              "This is most likely a temporary issue. Please try again in a few moments. If the problem persists, feel free to contact our support team for assistance. Thank you for your patience!"
                            )}
                            <br />
                            <br />
                            {projectCreateError && (
                              <span className="small">
                                Error Details:
                                <pre>{projectCreateError}</pre>
                              </span>
                            )}
                            {artworkUploadError && (
                              <span className="small">
                                Error Details:
                                <pre>{artworkUploadError}</pre>
                              </span>
                            )}
                          </p>
                        </div>
                      </ModalBody>
                      <ModalFooter>
                        <Button
                          color="primary"
                          onClick={() => {
                            dispatch(resetCreateFail());
                            dispatch(resetUploadFail());
                          }}
                        >
                          {context.t("Close")}
                        </Button>{" "}
                      </ModalFooter>
                    </Modal>

                    {(addToShopCartSuccess || addToCartProcessing) && (
                      <AddToCartModal
                        addItemToCart={(options) =>
                          dispatch(addItemToCart(options))
                        }
                        productCount={cart?.data?.attributes?.item_count}
                        language={language}
                        // FIXME maybe not the best place to do, but okay for production
                        extraProducts={
                          cart?.included?.filter((item) => {
                            return (
                              item?.type === "line_item" &&
                              item?.attributes?.sku === "7004"
                            );
                          }).length > 0
                            ? ["text"]
                            : ["text", "squeegee"]
                        }
                        ctaTitle={context.t("upload another design")}
                        addingToCart={addingToCart}
                        navigateTo={(url) => dispatch(push(url))}
                        addedToCart={addedToCart}
                        addToCartError={addToCartError}
                        addToShopCartSuccess={addToShopCartSuccess}
                        addToCartProcessing={addToCartProcessing}
                        ctaAction={resetForm}
                        resetCart={resetForm}
                      />
                    )}
                    <Modal
                      fade={false}
                      isOpen={addToCartWarningModal}
                      toggle={closeAddToCartWarning}
                      centered
                    >
                      <ModalHeader toggle={closeAddToCartWarning}>
                        {context.t("Warning")}
                      </ModalHeader>
                      <ModalBody>
                        <div>
                          <div className="d-flex justify-content-center mb-3 h1">
                            <FaExclamationTriangle
                              className=" fa-fw fa-5x text-warning"
                              aria-hidden="true"
                            />
                          </div>
                          <p>{renderFormWarnings()}</p>
                        </div>
                      </ModalBody>
                      <ModalFooter>
                        {width && height && (
                          <Button
                            color="primary"
                            outline
                            onClick={skipAddToCartWarning}
                          >
                            {context.t("Add to cart anyways")}
                          </Button>
                        )}{" "}
                        <Button color="primary" onClick={closeAddToCartWarning}>
                          {context.t("Close and adjust")}
                        </Button>{" "}
                      </ModalFooter>
                    </Modal>

                    <Modal
                      fade={false}
                      isOpen={addToCartErrorModal || !!addToCartError}
                      toggle={toggleAddToCartErrorModal}
                      centered
                    >
                      <ModalHeader toggle={toggleAddToCartErrorModal}>
                        {context.t("Alert")}
                      </ModalHeader>
                      <ModalBody>
                        <div>
                          <div className="d-flex justify-content-center mb-3 h1">
                            <FaTimes
                              className=" fa-fw fa-5x text-danger"
                              aria-hidden="true"
                            />
                          </div>
                          <p>{renderFormErrors()}</p>
                        </div>
                      </ModalBody>
                      <ModalFooter>
                        <Button
                          color="primary"
                          onClick={toggleAddToCartErrorModal}
                        >
                          {context.t("Close")}
                        </Button>{" "}
                      </ModalFooter>
                    </Modal>
                  </div>
                </div>
                <div className="small px-2 bg-light text-center">
                  {context.t("price per item")}:{" "}
                  <FormattedNumber
                    value={parseFloat(calculatedPrice.displayPrice)}
                    language={language}
                    unit="€"
                    decimals={2}
                  />
                  &#160;-&#160;
                  {context.t("incl. VAT")}
                  &#160;-&#160;
                  <a
                    href={`/${language}/p/versandarten/`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {context.t("plus shipping")}
                  </a>
                </div>
              </div>
            </Col>

            <Col md={{ size: 8 }} className="fileeditor__preview p-3">
              {!smallDevice && (
                <>
                  <h2 className="h6">{context.t("Your custom design file")}</h2>
                  <Row>
                    <Col
                      md={{ size: showSkipFileSection ? 6 : 12 }}
                      style={skip_file_upload ? { opacity: "0.5" } : {}}
                    >
                      {showSkipFileSection && (
                        <h3 className="h5">{context.t("Upload file now")}</h3>
                      )}
                      <h3 className="text-remark">{context.t("Preview")}</h3>
                      <FileUploadPreview
                        cropShape={
                          FILEEDITOR[parsedEditorType]
                            ? FILEEDITOR[parsedEditorType].cropShape
                            : false
                        }
                        allowedFormats={allowedFormats}
                        uploadFile={(args) => {
                          dispatch(uploadFile(args));
                        }}
                        handleUpdateSku={handleUpdateSku}
                        file={artwork?.data?.attributes}
                        forceWhiteBackground={
                          cutting === "rectangular" &&
                          parsedEditorType !==
                            FILEEDITORTYPES.STICKER_WHITEPRINT
                        }
                        formRatio={width / height}
                        sku={sku}
                        sizeChanged={sizeChanged}
                        acceptedMIMETypes={
                          "image/gif, image/jpeg, image/pjpeg, image/png, application/octet-stream, application/postscript, application/pdf, image/svg+xml, image/tiff, image/tiff-fx, image/vnd.adobe.photoshop, application/x-photoshop, application/photoshop, application/vnd.adobe.photoshop, application/psd, image/psd, image/*, .psd"
                        }
                      />
                    </Col>
                    <Col
                      md={{ size: showSkipFileSection ? 6 : 12 }}
                      className={`${showSkipFileSection ? "" : "bg-white"}`}
                    >
                      {showSkipFileSection && (
                        <>
                          <h3 className="h5">
                            {context.t("Upload File later")}
                          </h3>
                          <h3 className="text-remark">
                            {context.t("Proceed without file upload")}
                          </h3>
                          <p>
                            <strong>
                              {context.t(
                                "For Designers and Business Customers."
                              )}
                            </strong>
                            <br />
                            {context.t(
                              "If you are a designer or business customer you can also upload your file later. Please make sure that your file is created with the correct file dimensions and follows our design guidelines. You need to provide your file later as EPS, AI or PDF file. We are going to send you instructions by email for the fileupload after you completed your order."
                            )}
                            {context.t(
                              "Please read carefully our Guidelines and provide files according to it:"
                            )}{" "}
                            <a
                              href={`/${language}/fileguide/`}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              <img
                                src={fileguideIcon}
                                alt={context.t("Production Data Guidelines")}
                                height="20"
                              />{" "}
                              {context.t("Production Data Guidelines")}
                            </a>
                          </p>

                          <Label className="ml-3">
                            <Controller
                              name="skip_file_upload"
                              control={control}
                              render={({ field }) => (
                                <Input
                                  type={"checkbox"}
                                  checked={field.value}
                                  {...field}
                                />
                              )}
                            />
                            {context.t(
                              "I can provide Design Files and want to upload the files later."
                            )}
                          </Label>
                        </>
                      )}
                    </Col>
                  </Row>
                  {artwork?.data?.attributes?.width &&
                    !sections.includes("color") &&
                    (cutting === "rectangular" ||
                      cutting === "outline" ||
                      cutting === "exempted") && (
                      <div className="d-flex bg-white pt-1 px-2">
                        <em className="text-remark">
                          *
                          {parsedEditorType !==
                            FILEEDITORTYPES.STICKER_WHITEPRINT && (
                            <>
                              {context.t(
                                "Hint: We print your design on white vinyl."
                              )}
                              &nbsp;
                            </>
                          )}
                          {parsedEditorType ===
                            FILEEDITORTYPES.STICKER_WHITEPRINT && (
                            <>
                              {context.t(
                                "Hint: We print your design on transparent vinyl."
                              )}
                              &nbsp;
                            </>
                          )}
                          {cutting === "outline" &&
                            context.t(
                              "The preview does not represent the actual outline. Please also read the hint on the cutting selection."
                            )}
                        </em>
                      </div>
                    )}
                  <div className="d-flex bg-white p-3">
                    <img
                      src={instructions}
                      alt="size_instructions"
                      height="90px"
                      loading="lazy"
                    />
                    <div className="flex-fill">
                      <div>
                        <span className="small">
                          {context.t("Size of your vinyl (width x height):")}
                        </span>
                        <br />
                        <strong>
                          <FormattedNumber
                            value={Math.round(width * 100) / 100}
                            language={language}
                            unit="mm"
                            decimals={1}
                          />
                          &nbsp;x&nbsp;
                          <FormattedNumber
                            value={Math.round(height * 100) / 100}
                            language={language}
                            unit="mm"
                            decimals={1}
                          />
                          &nbsp;=<span> </span>
                          <FormattedNumber
                            value={Math.round(width * 10) / 100}
                            language={language}
                            unit="cm"
                            decimals={2}
                          />
                          &nbsp;x&nbsp;
                          <FormattedNumber
                            value={Math.round(height * 10) / 100}
                            language={language}
                            unit="cm"
                            decimals={2}
                          />
                        </strong>
                        {showSmallSizeWarning && (
                          <p className="small text-danger">
                            ⚠️{" "}
                            {context.t(
                              "The minimum size for height and width is 10mm "
                            )}
                          </p>
                        )}
                        {showMightBeDividedHint && (
                          <p className="small text-danger">
                            ℹ️{" "}
                            {context.t(
                              "For technical reasons, your design may need to be divided into several parts, as both dimensions exceed the maximum manufacturable size. It can be installed without any visible gap."
                            )}
                          </p>
                        )}
                      </div>
                      {dpiStars && (
                        <Row>
                          <Col xs={5}>{context.t("Quality at this size:")}</Col>
                          <Col xs={7} className={"text-" + starColor}>
                            {dpiStars} &nbsp; (
                            <FormattedNumber
                              value={Math.round(dpi * 100) / 100}
                              language={language}
                              decimals={1}
                            />
                            &nbsp;{context.t("DPI")})
                          </Col>
                        </Row>
                      )}
                    </div>
                  </div>
                </>
              )}
              <div className="fileeditor__preview-faq mt-3">
                <h4 className="h6">
                  {context.t("What files can be uploaded?")}
                </h4>
                <p className="small">
                  {context.t(
                    "You can upload AI, EPS, SVG, JPG, PSD, PNG and GIF files. The quality of your file is important for a perfect print and cutting result. We summarized the most important requirements for uploaded files with your design. If you are not sure about your file we recommend you to let us perform a file check. We recommend vectorized files such as EPS, AI or SVG."
                  )}
                </p>

                <Row>
                  <Col xs={2}>
                    <a
                      href={`/${language}/fileguide/`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-dark"
                    >
                      <img
                        className="w-100"
                        src={fileguideIcon}
                        alt={context.t("Production Data Guidelines")}
                      />
                    </a>
                  </Col>
                  <Col xs={10}>
                    <span className="h6">
                      <a
                        href={`/${language}/fileguide/`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-dark"
                      >
                        {context.t("Production Data Guidelines")}
                      </a>
                    </span>
                    <p className="small">
                      {context.t(
                        "Our Guidelines cover the most important aspecs on how your production data needs to be delivered. Please read it carefully and prepare your files accordingly. If your files are not aligned with the guidelines the result might look different than expected. In doubt we recommend adding a professional filecheck for each file that you want to have checked."
                      )}
                      <br />
                      <br />
                      <a
                        href={`/${language}/fileguide/`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        &raquo; {context.t("Production Data Guidelines")}
                      </a>
                    </p>
                  </Col>
                </Row>
              </div>
              <div className="fileeditor__preview-faq mt-3">
                <h4 className="h6">
                  {context.t("Why do I need a Vector file for some cuttings?")}
                </h4>
                <p className="small">
                  {context.t(
                    "Our plot machines follow a path to cut around your design. Those paths are included in vector files. Also it improves the quality of the print since vectors can be scaled almost limitless without any drop in quality."
                  )}
                </p>
              </div>
              <div className="fileeditor__preview-faq small text-right">
                <a
                  href={`https://www.myfolie.com/${language}/faqs/`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {context.t("More FAQs")}
                </a>
              </div>

              {/* FIXME Refactor to external file? START VECTORIZE MODAL */}
              <Modal
                fade={false}
                className="vectorize-file-modal"
                isOpen={vectorizeFileModal}
                size="lg"
                toggle={useUnvectorizedFileAndCloseModal}
                centered
              >
                <ModalHeader>{context.t("Vectorfile required")}</ModalHeader>
                <ModalBody>
                  <p>
                    {context.t(
                      "For the selected cutting a vector file is required."
                    )}
                  </p>
                  <Container>
                    <Row>
                      <Col md={6}>
                        <Card>
                          <CardImg
                            height="60"
                            src={icon_upload}
                            className="my-3"
                          />
                          <CardBody>
                            <CardTitle className="h5">
                              {context.t("Upload existing vector file")}
                            </CardTitle>
                            <CardText className="semi-small">
                              {context.t(
                                "If you already have a vectorized version of your design you can upload it here. Learn more about vector files in"
                              )}{" "}
                              <a href="/faqs" target="_blank">
                                {context.t("our FAQ")}.
                              </a>
                            </CardText>
                            <div>
                              <label
                                htmlFor="vector_file_upload"
                                className="btn btn-primary fileeditor__custom-file-upload-label"
                              >
                                {context.t("upload file")}
                              </label>
                              <input
                                type="file"
                                accept=".svg,.ai,.eps,.pdf"
                                id={"vector_file_upload"}
                                className="fileeditor__custom-file-upload-input"
                                onChange={handleModalFileUpload}
                              />
                            </div>
                          </CardBody>
                        </Card>
                      </Col>

                      <Col md={6}>
                        <Card>
                          <CardImg
                            height="60"
                            src={icon_vectorfile}
                            className="my-3"
                          />
                          <CardBody>
                            <div className="clearfix">
                              <CardTitle className="h5 pull-left">
                                {context.t("Add vectorization service")}
                              </CardTitle>
                              <CardSubtitle
                                className="h6 pull-right"
                                style={{ color: "green" }}
                              >
                                {context.t("from")}{" "}
                                <span className="h4">9,90€</span>
                              </CardSubtitle>
                            </div>
                            <CardText className="semi-small">
                              {context.t(
                                "Our designers vectorize your design. After your order is complete you'll receive the vector file for further usage."
                              )}
                            </CardText>
                            <Button
                              color="primary"
                              onClick={handleBookVectorization}
                            >
                              {context.t("Add service")}
                            </Button>
                          </CardBody>
                        </Card>
                        <p className="text-remark mt-2">
                          <strong>{context.t("Note:")}</strong>{" "}
                          {context.t(
                            "This paid service starts at 9,90€ (incl. tax). From our experience, this covers 95% of all cases. If your design is very complex additional charges may apply after consultation."
                          )}
                        </p>
                      </Col>
                    </Row>
                  </Container>
                </ModalBody>
                <ModalFooter style={{ background: "#efefef" }}>
                  <p className="text-remark col-8">
                    {context.t(
                      "I confirm that my uploaded file is already a vector file. I'm aware that my design can only be produced with a valid vector file."
                    )}
                    <br />
                    <a
                      href={`/${language}/fileguide/`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <img
                        src={fileguideIcon}
                        alt={context.t("Production Data Guidelines")}
                        height="12"
                      />{" "}
                      {context.t("Production Data Guidelines")}
                    </a>
                  </p>
                  <Button
                    className="col-4"
                    color="secondary"
                    size="sm"
                    outline
                    onClick={useUnvectorizedFileAndCloseModal}
                  >
                    {context.t("proceed with existing file")}
                  </Button>
                </ModalFooter>
              </Modal>

              {/* FIXME Refactor to external file? END VECTORIZE MODAL */}

              <FileSizeModal
                proposeOptimizedSizes={!sections.includes("sizeselect")}
              />
            </Col>
          </Row>
        </Container>
      </form>
    </FormProvider>
  );
};

/**  define proptype for the 'translation' function  */
FileUploadEditorForm.contextTypes = {
  t: PropTypes.func,
};

/** define proptypes for the form */
FileUploadEditorForm.propTypes = {};

export default FileUploadEditorForm;
